import type { Ear } from "../type/tonalTest";
import { getApiUrl } from "./getApiUrl";

// Cache raw ArrayBuffers keyed by word so each word is only fetched once.
const _audioCache = new Map<string, ArrayBuffer>();

/**
 * Fetches TTS audio for `word` from the backend, decodes it, and plays it
 * through an AudioContext with a StereoPannerNode so the sound is heard
 * only in the specified ear (pan = -1 for left, +1 for right).
 *
 * @returns a cancel function — call it to stop playback immediately.
 */
export function speakToEar(
  word: string,
  volume: number,
  ear: Ear,
  onEnd: () => void,
  onError: () => void
): () => void {
  let cancelled = false;
  let source: AudioBufferSourceNode | null = null;
  let ctx: AudioContext | null = null;

  const run = async () => {
    try {
      // ── Fetch (or return cached) raw audio bytes ──────────────────────────
      let raw = _audioCache.get(word);
      if (!raw) {
        const resp = await fetch(`${getApiUrl("tts")}?word=${encodeURIComponent(word)}&rate=-2`);
        if (!resp.ok) throw new Error(`TTS fetch failed: ${resp.status}`);
        raw = await resp.arrayBuffer();
        _audioCache.set(word, raw);
      }
      if (cancelled) return;

      // ── Decode audio ──────────────────────────────────────────────────────
      ctx = new AudioContext();
      // slice() because decodeAudioData detaches the buffer
      const audioBuffer = await ctx.decodeAudioData(raw.slice(0));
      if (cancelled) { void ctx.close(); return; }

      // ── Build audio graph: source → gain → panner → output ───────────────
      const gainNode = ctx.createGain();
      gainNode.gain.value = Math.max(0, Math.min(1, volume));

      const panner = new StereoPannerNode(ctx, { pan: ear === "left" ? -1 : 1 });

      source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(gainNode).connect(panner).connect(ctx.destination);

      source.onended = () => {
        void ctx?.close();
        ctx = null;
        if (!cancelled) onEnd();
      };

      source.start();
    } catch {
      void ctx?.close();
      ctx = null;
      if (!cancelled) onError();
    }
  };

  void run();

  // Return cancel function
  return () => {
    cancelled = true;
    try { source?.stop(); } catch { /* already stopped */ }
    void ctx?.close();
    ctx = null;
  };
}

/** Pre-warm the cache for a list of words (optional, call before test starts). */
export async function preloadWords(words: string[]): Promise<void> {
  await Promise.allSettled(
    words
      .filter(w => !_audioCache.has(w))
      .map(async w => {
        const resp = await fetch(`${getApiUrl("tts")}?word=${encodeURIComponent(w)}&rate=-2`);
        if (resp.ok) _audioCache.set(w, await resp.arrayBuffer());
      })
  );
}
